import { useState, useRef, useEffect } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import TiptapLink from "@tiptap/extension-link";
import CodeTiptap from "@tiptap/extension-code";
import { Markdown as TiptapMarkdown } from "tiptap-markdown";

/** Helper: get markdown string from editor via tiptap-markdown */
export const getEditorMarkdown = (editor) => {
  if (!editor) return "";
  return (editor.storage.markdown?.getMarkdown() || "").trim();
};

/* Colors reference CSS vars from the tailwind semantic-colors plugin,
   so dark/light switching is automatic via the `.dark` class.
   No placeholder rule — the editor renders an empty paragraph and the
   native caret blinks against it; that is the intended idle state. */
const TIPTAP_STYLES = `
  .tiptap-input .ProseMirror {
    min-height: 32px;
    max-height: 50vh;
    overflow-y: auto;
    caret-color: rgb(var(--c-mainText));
  }
  .tiptap-input .ProseMirror:focus {
    outline: none;
  }
  .tiptap-input .ProseMirror p {
    margin: 0;
  }
  .tiptap-input .ProseMirror strong {
    font-weight: 700;
  }
  .tiptap-input .ProseMirror code {
    background: rgb(var(--c-mainButtonBg));
    padding: 1px 5px;
    border-radius: 4px;
    font-family: ui-monospace, monospace;
    font-size: 13px;
  }
  .tiptap-input .ProseMirror a {
    color: rgb(var(--c-velBlue));
    text-decoration: underline;
  }
`;

const TiptapInput = (props) => {
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [tempEntry, setTempEntry] = useState("");
  const editorCallbackFired = useRef(false);

  const editor = useEditor({
    // No autofocus on init — focus is gated on isLoadingThread below so
    // we don't steal focus while an existing thread's history is still
    // being fetched. New threads (isLoadingThread=false) focus immediately
    // via the same effect.
    extensions: [
      StarterKit.configure({
        heading: false,
        blockquote: false,
        bulletList: false,
        orderedList: false,
        listItem: false,
        codeBlock: false,
        horizontalRule: false,
        hardBreak: { keepMarks: true },
      }),
      CodeTiptap,
      TiptapLink.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: "text-velBlue underline cursor-pointer",
        },
      }),
      TiptapMarkdown.configure({
        html: false,
        transformPastedText: true,
        transformCopiedText: true,
      }),
    ],
    editorProps: {
      attributes: {
        class: [
          // 32px min-height = roughly one line of 14px / leading-relaxed
          // text plus the py-1 padding. Grows up to max-h-[50vh] as the
          // user types and wraps.
          "w-full min-h-[32px] max-h-[50vh] overflow-y-auto px-1 py-1",
          // Softened from text-mainText → text-mainText/90 so input copy
          // doesn't feel glaring against the surface.
          "text-[14px] leading-relaxed text-mainText/90",
          "focus:outline-none",
          "prose prose-sm dark:prose-invert max-w-none",
        ].join(" "),
      },
      handleKeyDown: (view, event) => {
        if (event.key === "Enter" && !event.shiftKey) {
          event.preventDefault();
          const { editor: ed } = props.editorRef?.current || {};
          const md = ed ? getEditorMarkdown(ed) : "";
          if (md.trim()) {
            props.sendPayload(
              md,
              props.selectedAgent?.apiName,
              props.currentThreadId,
            );
            if (ed) {
              requestAnimationFrame(() => ed.commands.clearContent());
            }
          }
          return true;
        }

        // Arrow up/down for message history
        const history = props.sentHistory || [];
        if (history.length === 0) return false;

        const { state } = view;
        const { from, to } = state.selection;
        const atStart = from === 1 && to === 1;
        const atEnd = from === state.doc.content.size - 1;

        if (event.key === "ArrowUp" && atStart) {
          event.preventDefault();
          const idx = historyIndex;
          const { editor: ed } = props.editorRef?.current || {};
          if (idx === -1 && ed) {
            setTempEntry(getEditorMarkdown(ed));
          }
          const newIdx = Math.min(idx + 1, history.length - 1);
          setHistoryIndex(newIdx);
          props.setChatEntry(history[newIdx]);
          if (ed) {
            requestAnimationFrame(() => {
              ed.commands.setContent(history[newIdx]);
            });
          }
          return true;
        }
        if (event.key === "ArrowDown" && atEnd) {
          event.preventDefault();
          const idx = historyIndex;
          const { editor: ed } = props.editorRef?.current || {};
          if (idx <= 0) {
            setHistoryIndex(-1);
            props.setChatEntry(tempEntry);
            if (ed) {
              requestAnimationFrame(() => {
                ed.commands.setContent(tempEntry || "");
              });
            }
          } else {
            const newIdx = idx - 1;
            setHistoryIndex(newIdx);
            props.setChatEntry(history[newIdx]);
            if (ed) {
              requestAnimationFrame(() => {
                ed.commands.setContent(history[newIdx]);
              });
            }
          }
          return true;
        }
        return false;
      },
    },
    onUpdate: ({ editor: ed }) => {
      const md = getEditorMarkdown(ed);
      props.setChatEntry(md);
      if (props.onInputChange) {
        props.onInputChange(md);
      }
    },
  });

  useEffect(() => {
    if (editor && props.editorRef && !editorCallbackFired.current) {
      props.editorRef.current = { editor };
      editorCallbackFired.current = true;
    }
  }, [editor, props.editorRef]);

  useEffect(() => {
    if (editor && props.editorRef) {
      props.editorRef.current = { editor };
    }
  }, [editor]);

  // Focus rule:
  //   - New thread (isLoadingThread=false from the start) → focuses on
  //     mount immediately, since nothing is being fetched.
  //   - Existing thread (isLoadingThread=true while history loads) →
  //     skips the early fire and only focuses after the flag flips
  //     false, so the cursor lands on a settled view rather than the
  //     skeleton.
  // currentThreadId is included so a thread→thread switch re-arms the
  // rule for the new id.
  useEffect(() => {
    if (!editor) return;
    if (props.isLoadingThread) return;
    editor.commands.focus();
  }, [props.currentThreadId, props.isLoadingThread, editor]);

  const lastExternalSync = useRef("");
  useEffect(() => {
    if (!editor) return;
    const currentMd = getEditorMarkdown(editor);
    if (
      props.chatEntry !== currentMd &&
      props.chatEntry !== lastExternalSync.current
    ) {
      lastExternalSync.current = props.chatEntry;
      if (props.chatEntry === "") {
        editor.commands.clearContent();
        setHistoryIndex(-1);
        setTempEntry("");
      } else {
        editor.commands.setContent(props.chatEntry);
      }
    }
  }, [props.chatEntry, editor]);

  if (!editor) return null;

  return (
    <div className="tiptap-input">
      <EditorContent editor={editor} />
      <style>{TIPTAP_STYLES}</style>
    </div>
  );
};

export default TiptapInput;
